import { predicates } from "../predicates";
import type { ObjectTypeConfig } from "../types";

export const FILE_RECORD: ObjectTypeConfig = {
  actions: [
    {
      id: "open",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
    },
    {
      // A3: canvas/chat files only — a Library file is already there, a
      // reference points at one, and folders promote file-by-file for now.
      id: "add-to-library",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      hiddenWhen: {
        any: [
          { kind: { type: "file-record", class: "workspace" } },
          { kind: { type: "file-record", isReference: true } },
          { kind: { type: "file-record", isFolder: true } },
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      // The tier marker mirroring add-to-library: a Library file (or a
      // reference to one) states its home instead of offering promotion.
      id: "in-library",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-more-submenu",
      },
      hiddenWhen: {
        any: [
          // On the rail and the workspace pages the marker states where you
          // already are. It earns its place on a canvas, where "this lives
          // in the Library" is news; elsewhere it is a disabled row saying
          // nothing.
          {
            all: [
              { kind: { type: "file-record", class: "workspace" } },
              {
                any: [
                  { surface: "left-rail" },
                  { surface: "main-view" },
                  { surface: "context-menu" },
                ],
              },
            ],
          },
          {
            all: [
              { not: { kind: { type: "file-record", class: "workspace" } } },
              { not: { kind: { type: "file-record", isReference: true } } },
              // Node surfaces resolve with the canvas-node kind — a reference
              // appearance there is the same Library tier.
              {
                not: { kind: { type: "canvas-node", entityIsReference: true } },
              },
            ],
          },
        ],
      },
      // A marker, never a verb — disabled on every kind it renders for.
      disabledWhen: {
        any: [
          { kind: { type: "file-record" } },
          { kind: { type: "canvas-node" } },
        ],
      },
    },
    {
      id: "star",
      surfaces: ["left-rail", "main-view", "context-menu", "card-overlay"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "card-overlay": "corner-star",
      },
      hiddenWhen: {
        any: [
          predicates.isFavorite,
          { kind: { type: "file-record", isReference: true } },
          { kind: { type: "file-record", class: "canvas" } },
          { kind: { type: "file-record", class: "flow-session" } },
        ],
      },
    },
    {
      id: "unstar",
      surfaces: ["left-rail", "main-view", "context-menu", "card-overlay"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "card-overlay": "corner-star",
      },
      hiddenWhen: {
        any: [
          { not: predicates.isFavorite },
          { kind: { type: "file-record", isReference: true } },
          { kind: { type: "file-record", class: "canvas" } },
          { kind: { type: "file-record", class: "flow-session" } },
        ],
      },
    },

    {
      id: "share",
      surfaces: [
        "left-rail",
        "main-view",
        "context-menu",
        "floating-toolbar",
        "pane-header",
      ],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
        "pane-header": "secondary-button",
      },
      // Sharing acts on the real file; a reference resolves to its target
      // server-side, so the verb stays available everywhere the file shows.
      hiddenWhen: predicates.cannotShare,
    },
    {
      id: "publish",
      surfaces: [
        "left-rail",
        "main-view",
        "context-menu",
        "floating-toolbar",
        "pane-header",
      ],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
        "pane-header": "primary-button",
      },
      // Publishing through a reference publishes the TARGET (one publish
      // state per real file) — resolved server-side, so no isReference gate.
      hiddenWhen: { not: { state: "showPublish" } },
    },
    {
      // Only offer Copy URL once the file is published — it copies the public
      // share URL, not the internal one. An unpublished file has no useful
      // public link to hand out.
      id: "copy-url",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      // The reference renders its target's publish state, so Copy URL
      // follows the target's published flag like every other surface.
      hiddenWhen: { not: { state: "isPublished" } },
    },
    {
      id: "download",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      variantSource: "file-record-export",
    },
    {
      id: "rename",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      // A reference always displays its target's live name — rename the
      // Library file instead.
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          { kind: { type: "file-record", isReference: true } },
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "move",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          { kind: { type: "file-record", isReference: true } },
          // Allowlist, not a container blocklist: canvas-node entities can be
          // minimal shapes with no class, and those must read as container
          // files (whose one route out is Add to Library), never as movable.
          { not: { kind: { type: "file-record", class: "workspace" } } },
        ],
      },
      disabledWhen: predicates.cannotMove,
    },
    {
      // Copy to travels with Move to, on Library files only — a container
      // file's one route out is Add to Library (A3), and a reference is an
      // appearance rather than a file. Deliberately carries no edit guard:
      // copying only reads the source, so viewing it is enough; write access
      // is judged on the destination, inside the picker and again server-side.
      id: "copy-to",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          { kind: { type: "file-record", isReference: true } },
          // Same allowlist as move: only a Library file copies from here.
          { not: { kind: { type: "file-record", class: "workspace" } } },
        ],
      },
    },
    // Hide / Show on canvas — the non-destructive pair. Hide surfaces on the
    // canvas node menus (context menu + toolbar More) and on explorer rail
    // rows; Show only makes sense from the rail (a hidden node can't be
    // right-clicked on the board).
    {
      id: "remove-from-canvas",
      surfaces: ["left-rail", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-more-submenu",
      },
      hiddenWhen: {
        any: [
          {
            all: [
              { not: { scope: "canvas-explorer" } },
              { not: { state: "onCanvasDetail" } },
            ],
          },
          { state: "hiddenOnCanvas" },
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "show-on-canvas",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          { not: { scope: "canvas-explorer" } },
          { not: { state: "hiddenOnCanvas" } },
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "delete",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          { state: "onCanvasDetail" },
        ],
      },
      disabledWhen: predicates.cannotDelete,
    },

    {
      id: "version-history",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          { not: { state: "showVersionHistory" } },
          { kind: { type: "file-record", isReference: true } },
        ],
      },
    },
    {
      id: "locate-on-canvas",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: { not: { scope: "canvas-explorer" } },
    },
  ],
};

export const FILE_RECORD_FOLDER: ObjectTypeConfig = {
  actions: [
    {
      id: "open",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
    },
    {
      id: "rename",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "move",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      disabledWhen: predicates.cannotEdit,
    },
    {
      // Copy to travels with Move to on Library folders. The copy cascades
      // over the folder's contents client-side.
      id: "copy-to",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
    },
    // Container actions — only where files can be uploaded (files tab folder rows).
    {
      id: "new-folder",
      surfaces: ["left-rail", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.notFileUploadContext,
    },
    {
      id: "upload-files",
      surfaces: ["left-rail", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.notFileUploadContext,
    },
    {
      id: "new-document",
      surfaces: ["left-rail", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.notFileUploadContext,
    },
    {
      id: "delete",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      disabledWhen: predicates.cannotDelete,
    },
  ],
};
